/**
 * The query parameter `extern-website` forwards its Segment anonymous id in.
 *
 * A contract with the other repo, where the same constant lives in
 * `lib/analytics/anonymous-id.ts`. The name is `analytics.js`'s own — it is what
 * the library looks for in the query string to adopt an anonymous id — but the
 * adoption here is explicit rather than left to the library, because whether the
 * build served by `segment-analytics.extern.com` honours it is not something
 * this app should depend on.
 */
export const SEGMENT_ANONYMOUS_ID_PARAM = 'ajs_aid';

/**
 * Set by the Segment snippet, on the page loads where it adopted a forwarded id.
 *
 * It exists because the snippet's work is *queued*, not done. When it resets and adopts, both calls
 * sit in the stub's buffer until `analytics.min.js` arrives — so `ajs_user_id` is still in storage,
 * still showing the previous occupant, to anything that looks. `discardStaleIdentity` looks, and
 * without this flag it would conclude the browser is stale and queue a second `reset()`. That reset
 * flushes *after* the adoption and rolls a fresh anonymous id, throwing away the very id the
 * hand-off exists to carry.
 *
 * A boolean on `window` rather than a URL check: `stripForwardedAnonymousId` removes the parameter
 * before the identify runs, so by then the URL no longer says what happened.
 *
 * It says only that this page load began with a hand-off, and it says so for as long as the SPA
 * session lasts — so `discardStaleIdentity` pairs it with the occupant it found and stands down for
 * that one alone. A different account identified later is ordinary staleness and gets the full
 * guard. The PostHog half of the hand-off's reset lives there too: an inline tag runs long before
 * `posthog.init`, so the snippet can only reset Segment.
 */
export const SEGMENT_IDENTITY_ADOPTED_FLAG = '__externSegmentIdentityAdopted';

/** Whether the snippet already settled this page load's identity. See the flag above. */
export function hasAdoptedForwardedAnonymousId(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return (window as unknown as Record<string, unknown>)[SEGMENT_IDENTITY_ADOPTED_FLAG] === true;
}

/**
 * Takes the forwarded id out of the address bar once it has been adopted.
 *
 * It is pseudonymous rather than personal, and it travels to a host of ours —
 * but a query parameter is the one place an identifier keeps being copied from:
 * into `document.referrer` on the next outbound link, into access logs, into a
 * URL someone pastes into a ticket. Adopting it is the whole point; keeping it
 * visible afterwards buys nothing.
 *
 * `history.replaceState` rather than `router.replace`: this runs inside a route
 * that guards itself against re-running, and a Next navigation would remount it.
 */
/**
 * The query string with the forwarded id taken out.
 *
 * Two callers, both of which would otherwise put the id back after it has been
 * stripped from the address bar:
 *
 * - `<PageView />` builds the `url` it reports from the live search string.
 *   Leaving the id in means shipping it to Mixpanel as an event property — and,
 *   because Next patches `history.replaceState` to sync the router, it also
 *   means the strip below reads as a new URL and sends a second page view for
 *   one visit.
 * - the callback's `/sign-in` return URL, which is rebuilt from the params it
 *   arrived with, is written into a link and then into access logs.
 */
export function searchWithoutForwardedAnonymousId(searchParams: URLSearchParams | null | undefined): string {
  if (!searchParams) {
    return '';
  }

  const params = new URLSearchParams(searchParams);
  params.delete(SEGMENT_ANONYMOUS_ID_PARAM);

  return params.toString();
}

export function stripForwardedAnonymousId(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);

  if (!url.searchParams.has(SEGMENT_ANONYMOUS_ID_PARAM)) {
    return;
  }

  url.searchParams.delete(SEGMENT_ANONYMOUS_ID_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}
